import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash2, Plus, Home, Building2, FileText, Loader2 } from "lucide-react";
import plannerApi, { planHome } from "@/lib/plannerApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { PlannerShell } from "@/components/planner/PlannerShell";
import { QuoteStatusPill } from "@/components/planner/QuoteStatusPill";
import { fmtINR } from "@/lib/portalApi";

export default function PlannerLists() {
    const { client } = usePortalAuth();
    const nav = useNavigate();
    const [lists, setLists] = useState(null);
    const [quotes, setQuotes] = useState([]);
    const load = () => plannerApi.get("/lists").then((r) => setLists(r.data)).catch(() => setLists([]));
    useEffect(() => { load(); }, []);
    useEffect(() => { if (client) plannerApi.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {}); }, [client]);

    const dup = async (l) => {
        const month = new Date().toISOString().slice(0, 7);
        try {
            const r = await plannerApi.post(`/lists/${l.id}/duplicate`, { cycle_month: month });
            toast.success(`Copied for ${month} — edit only what's different`);
            nav(`/portal/essentials-planner/list/${r.data.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't copy"); }
    };
    const del = async (l) => {
        if (!window.confirm(`Delete “${l.name}”?`)) return;
        await plannerApi.delete(`/lists/${l.id}`);
        load();
    };
    const groups = [["Master lists", (l) => l.list_type === "MASTER"], ["Monthly lists", (l) => l.list_type === "CYCLE"]];

    return (
        <PlannerShell>
            <div data-testid="planner-lists">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <h1 className="text-3xl sm:text-4xl font-black text-slate-900" style={{ fontFamily: "'Chivo',sans-serif" }}>My lists</h1>
                    <div className="flex gap-2">
                        <Link to={planHome("HOME")} className="hdb-btn hdb-btn-ghost" data-testid="planner-new-home"><Home size={13}/> Home list</Link>
                        <Link to={planHome("BUSINESS")} className="hdb-btn hdb-btn-ghost" data-testid="planner-new-business"><Building2 size={13}/> Business list</Link>
                    </div>
                </div>
                {!client && <p className="text-xs text-slate-500 mt-2">Saved on this device. Sign in to keep your lists on every device.</p>}
                {lists === null ? <div className="p-10 text-center"><Loader2 className="inline animate-spin text-slate-400"/></div> : (
                    lists.length === 0 ? (
                        <div className="mt-8 rounded-2xl bg-white border border-dashed border-slate-300 p-10 text-center" data-testid="planner-lists-empty">
                            <p className="text-slate-500">No lists yet.</p>
                            <Link to="/portal/essentials-planner" className="mt-3 inline-flex items-center gap-1 text-emerald-700 font-bold"><Plus size={14}/> Start planning</Link>
                        </div>
                    ) : groups.map(([title, f]) => lists.filter(f).length > 0 && (
                        <section key={title} className="mt-8">
                            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{title}</div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {lists.filter(f).map((l, i) => (
                                    <div key={l.id} className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3" data-testid={`planner-list-card-${l.id}`}>
                                        <Link to={`/portal/essentials-planner/list/${l.id}`} className="min-w-0 flex-1" data-testid={`planner-list-open-${i}`}>
                                            <div className="font-bold text-slate-900 truncate">{l.name}</div>
                                            <div className="text-xs text-slate-500">{l.planner_type === "HOME" ? "Home" : "Business"} · {l.item_count} items{l.cycle_month ? ` · ${l.cycle_month}` : ""}</div>
                                        </Link>
                                        <button onClick={() => dup(l)} title="Copy for this month" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`planner-list-dup-${l.id}`}><Copy size={15}/></button>
                                        <button onClick={() => del(l)} title="Delete" className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`planner-list-del-${l.id}`}><Trash2 size={15}/></button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))
                )}
                {quotes.length > 0 && (
                    <section className="mt-10">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">Quotations</div>
                        <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
                            {quotes.map((q) => (
                                <Link key={q.id} to={`/portal/essentials-planner/quotes/${q.id}`} className="flex items-center gap-3 p-4 hover:bg-slate-50" data-testid={`planner-quote-row-${q.id}`}>
                                    <FileText size={16} className="text-slate-400"/>
                                    <div className="flex-1 min-w-0"><div className="font-bold text-sm">{q.number} · {q.list_name}</div>
                                        <div className="text-xs text-slate-500">{q.snapshot.lines.length} items · {q.submitted_at.slice(0, 10)}</div></div>
                                    {q.quote && <span className="font-mono text-sm">{fmtINR(q.quote.total)}</span>}
                                    <QuoteStatusPill status={q.status}/>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </PlannerShell>
    );
}
